import {
  BadgeCheck,
  Briefcase,
  Building,
  Building2,
  ChartBar,
  CircleDollarSign,
  Globe,
  GraduationCap,
  Heart,
  MapPin,
  Star,
  TreePine,
  TrendingUp,
  Truck,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import {
  CityProgram,
  CityProgramService,
} from "@/services/landing/cityProgramService";
import {
  CommunityProgram,
  CommunityProgramService,
} from "@/services/landing/communityProgramService";
import {
  CommunityVoice,
  CommunityVoiceService,
} from "@/services/landing/communityVoiceService";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";
import {
  ImpactStat,
  ImpactStatService,
} from "@/services/landing/impactStatService";
import {
  Partnership,
  PartnershipService,
} from "@/services/landing/partnershipService";

export default function ForCommunities() {
  const [communityPrograms, setCommunityPrograms] = useState<
    CommunityProgram[]
  >([]);
  const [cityPrograms, setCityPrograms] = useState<CityProgram[]>([]);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [communityVoices, setCommunityVoices] = useState<CommunityVoice[]>([]);
  const [impactStats, setImpactStats] = useState<ImpactStat[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [currentCitySlide, setCurrentCitySlide] = useState(0);
  const [currentVoiceSlide, setCurrentVoiceSlide] = useState(0);
  const [currentPartnershipSlide, setCurrentPartnershipSlide] = useState(0);
  const [currentStatSlide, setCurrentStatSlide] = useState(0);
  const itemsPerPage = 4;
  const partnershipsPerSlide = 4; // Will show 4 on lg, 2 on md, 1 on mobile
  const statsPerSlide = 4;
  const [loading, setLoading] = useState(true);
  const [cityLoading, setCityLoading] = useState(true);
  const [partnershipLoading, setPartnershipLoading] = useState(true);
  const [voicesLoading, setVoicesLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);
  const [cityError, setCityError] = useState<null | string>(null);
  const [partnershipError, setPartnershipError] = useState<null | string>(null);
  const [voicesError, setVoicesError] = useState<null | string>(null);
  const [statsError, setStatsError] = useState<null | string>(null);

  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  useEffect(() => {
    const loadPrograms = async () => {
      try {
        setLoading(true);
        const data = await CommunityProgramService.getAll();
        setCommunityPrograms(data);
      } catch (err) {
        console.error("Failed to fetch community programs:", err);
        setError("Failed to load community programs. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    const loadCityPrograms = async () => {
      try {
        setCityLoading(true);
        const data = await CityProgramService.getAll();
        setCityPrograms(data);
      } catch (err) {
        console.error("Failed to fetch city programs:", err);
        setCityError("Failed to load city programs. Please try again later.");
      } finally {
        setCityLoading(false);
      }
    };

    const loadPartnerships = async () => {
      try {
        setPartnershipLoading(true);
        const data = await PartnershipService.getAll();
        setPartnerships(data);
      } catch (err) {
        console.error("Failed to fetch partnerships:", err);
        setPartnershipError(
          "Failed to load partnerships. Please try again later.",
        );
      } finally {
        setPartnershipLoading(false);
      }
    };

    const loadCommunityVoices = async () => {
      try {
        setVoicesLoading(true);
        const data = await CommunityVoiceService.getAll();
        setCommunityVoices(data);
      } catch (err) {
        console.error("Failed to fetch community voices:", err);
        setVoicesError(
          "Failed to load community voices. Please try again later.",
        );
      } finally {
        setVoicesLoading(false);
      }
    };

    const loadImpactStats = async () => {
      try {
        setStatsLoading(true);
        const data = await ImpactStatService.getAll();
        setImpactStats(data);
      } catch (err) {
        console.error("Failed to fetch impact stats:", err);
        setStatsError("Failed to load impact stats. Please try again later.");
      } finally {
        setStatsLoading(false);
      }
    };

    loadPrograms();
    loadCityPrograms();
    loadPartnerships();
    loadCommunityVoices();
    loadImpactStats();

    fetchHeroBackgrounds("communities").then((data) => {
      setHeroBackground(data);
      setHeroLoading(false);
    });
  }, []);

  // Auto-play for city programs slider
  useEffect(() => {
    if (cityPrograms.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentCitySlide((prev) => (prev + 1) % cityPrograms.length);
    }, 5000); // Change slide every 5 seconds
    return () => clearInterval(interval);
  }, [cityPrograms.length]);

  // Auto-play for community voices slider
  useEffect(() => {
    if (communityVoices.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentVoiceSlide((prev) => (prev + 1) % communityVoices.length);
    }, 5000); // Change slide every 5 seconds
    return () => clearInterval(interval);
  }, [communityVoices.length]);

  // Helper function to get icon component based on icon name
  const getIconComponent = (iconName: string) => {
    const iconMap: { [key: string]: any } = {
      HiAcademicCap: GraduationCap,
      HiBadgeCheck: BadgeCheck,
      HiBriefcase: Briefcase,
      HiBuilding: Building,
      HiChartBar: ChartBar,
      HiCurrencyDollar: CircleDollarSign,
      HiGlobe: Globe,
      HiHeart: Heart,
      HiLightningBolt: Zap,
      HiLocationMarker: MapPin,
      HiOfficeBuilding: Building2,
      HiStar: Star,
      HiTrendingUp: TrendingUp,
      HiTruck: Truck,
      HiUsers: Users,
      HiZap: Zap,
    };
    const IconComponent = iconMap[iconName] || Users;
    return <IconComponent size={32} />;
  };

  // Pagination calculations for community programs
  const totalPages = Math.ceil(communityPrograms.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPrograms = communityPrograms.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to community programs section
    document
      .getElementById("community-programs")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  // Pagination for partnerships
  const totalPartnershipSlides = Math.ceil(
    partnerships.length / partnershipsPerSlide,
  );
  const partnershipStartIndex = currentPartnershipSlide * partnershipsPerSlide;
  const partnershipEndIndex = partnershipStartIndex + partnershipsPerSlide;
  const visiblePartnerships = partnerships.slice(
    partnershipStartIndex,
    partnershipEndIndex,
  );

  // Pagination for impact stats
  const totalStatSlides = Math.ceil(impactStats.length / statsPerSlide);
  const statStartIndex = currentStatSlide * statsPerSlide;
  const statEndIndex = statStartIndex + statsPerSlide;
  const visibleStats = impactStats.slice(statStartIndex, statEndIndex);

  const impactAreas = [
    {
      description:
        "Creating income opportunities for drivers, delivery partners, and local businesses",
      icon: <TrendingUp className="text-primary" size={32} />,
      stats: "25K+ earning partners",
      title: "Economic Empowerment",
    },
    {
      description:
        "Reducing carbon emissions through shared mobility and electric vehicles",
      icon: <TreePine className="text-success" size={32} />,
      stats: "30% emission reduction",
      title: "Environmental Impact",
    },
    {
      description:
        "Training programs for drivers, entrepreneurs, and digital literacy",
      icon: <GraduationCap className="text-info" size={32} />,
      stats: "10K+ people trained",
      title: "Skills Development",
    },
    {
      description:
        "Supporting local initiatives, education, and social development projects",
      icon: <Heart className="text-warning" size={32} />,
      stats: "R5M+ invested",
      title: "Community Support",
    },
  ];

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="relative -mt-16 overflow-hidden pt-16">
          <div className="absolute inset-0 opacity-20">
            {heroLoading ? (
              <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-success/10 to-primary/10">
                <div
                  className="animate-shimmer absolute inset-0 bg-gradient-to-r from-success/10 via-primary/10 to-success/10"
                  style={{ backgroundSize: "200% 100%" }}
                />
              </div>
            ) : heroBackground?.imageUrl ? (
              <img
                alt={heroBackground?.imageAlt || "Hero background"}
                className="h-full w-full object-cover"
                src={heroBackground.imageUrl}
              />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-success/5 to-primary/5" />
            )}
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-24">
            <div className="max-w-4xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-success-content/20 px-4 py-2">
                <Heart size={20} />
                <span className="font-medium">Impacting many Communities</span>
              </div>

              <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl">
                Building stronger{" "}
                <span className="text-primary">communities together</span>
              </h1>

              <p className="mb-8 text-xl leading-relaxed opacity-90">
                At Hareeng, we believe in creating shared value. Beyond
                mobility, we are committed to empowering communities, supporting
                local economies, and building a sustainable future for all.
              </p>

              <div className="flex flex-col gap-4 sm:flex-row">
                <a
                  className="btn btn-primary btn-lg"
                  href="#community-programs"
                >
                  See Our Impact
                </a>
                <a className="btn btn-outline btn-lg" href="/contact-us">
                  Partner With Us
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Areas */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Our Community Impact</h2>
              <p className="text-xl text-base-content/70">
                Creating positive change across multiple dimensions
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {impactAreas.map((area, index) => (
                <div className="card bg-base-100 shadow-lg" key={index}>
                  <div className="card-body text-center">
                    <div className="mx-auto mb-4">{area.icon}</div>
                    <h3 className="card-title mb-2 justify-center">
                      {area.title}
                    </h3>
                    <p className="mb-4 text-base-content/70">
                      {area.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Community Programs */}
        <section className="bg-base-100 py-16" id="community-programs">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Community Programs</h2>
              <p className="text-xl text-base-content/70">
                Initiatives designed to empower and uplift communities
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : error ? (
              <div className="alert alert-error">
                <span>{error}</span>
              </div>
            ) : (
              <>
                <div className="grid gap-8 md:grid-cols-2">
                  {paginatedPrograms.map((program) => (
                    <div
                      className="card bg-base-200 shadow-lg"
                      key={program.id}
                    >
                      <figure className="h-48">
                        <img
                          alt={program.imageAlt || program.title}
                          className="h-full w-full object-cover"
                          src={`${import.meta.env.VITE_API_STORAGE_URL}/storage/${program.uploadedImageUrl}`}
                        />
                      </figure>
                      <div className="card-body">
                        <h3 className="card-title">{program.title}</h3>
                        <p className="text-base-content/80">
                          {program.description}
                        </p>

                        <div className="mt-4 flex items-center justify-between">
                          <div className="text-sm">
                            <div className="font-semibold text-primary">
                              {program.beneficiaries.count.toLocaleString()}+{" "}
                              {program.beneficiaries.label}
                            </div>
                            <div className="text-base-content/70">
                              Beneficiaries
                            </div>
                          </div>
                          <div className="text-right text-sm">
                            <div className="font-semibold text-success">
                              {program.impact.value} {program.impact.label}
                            </div>
                            <div className="text-base-content/70">Impact</div>
                          </div>
                        </div>

                        {program.buttonText && program.buttonUrl && (
                          <div className="card-actions mt-4">
                            <a
                              className="btn btn-primary btn-sm"
                              href={program.buttonUrl}
                            >
                              {program.buttonText}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <div className="join">
                      <button
                        className="btn join-item"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        «
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <button
                            className={`btn join-item ${
                              currentPage === page ? "btn-active" : ""
                            }`}
                            key={page}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        ),
                      )}
                      <button
                        className="btn join-item"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        »
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* City Programs */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Programs by City</h2>
              <p className="text-xl text-base-content/70">
                Tailored initiatives for each community we serve
              </p>
            </div>

            {cityLoading ? (
              <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : cityError ? (
              <div className="alert alert-error">
                <span>{cityError}</span>
              </div>
            ) : (
              <div className="relative">
                <div className="overflow-hidden">
                  <div
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{
                      transform: `translateX(-${currentCitySlide * 100}%)`,
                    }}
                  >
                    {cityPrograms.map((city) => (
                      <div className="w-full flex-shrink-0 px-4" key={city.id}>
                        <div className="card mx-auto max-w-2xl bg-base-100 shadow-lg">
                          <div className="card-body">
                            <div className="mb-4 flex items-center gap-3">
                              <MapPin className="text-primary" size={24} />
                              <div>
                                <h3 className="card-title">{city.cityName}</h3>
                                <p className="text-sm text-base-content/70">
                                  {city.residents.count}
                                  {city.residents.unit} residents
                                </p>
                              </div>
                            </div>

                            <div className="mb-4">
                              <h4 className="mb-2 font-semibold">
                                Active Programs:
                              </h4>
                              <div className="flex flex-wrap gap-1">
                                {city.programs.map((program, pIndex) => (
                                  <span className="badge badge-sm" key={pIndex}>
                                    {program}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <div className="mt-auto">
                              <div className="text-lg font-bold text-success">
                                {city.totalImpact.value}{" "}
                                {city.totalImpact.label}
                              </div>
                              <div className="text-sm text-base-content/70">
                                Total impact
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {cityPrograms.length > 1 && (
                  <>
                    {/* Navigation Buttons */}
                    <button
                      className="btn btn-circle btn-sm absolute left-0 top-1/2 -translate-y-1/2"
                      onClick={() =>
                        setCurrentCitySlide(
                          (prev) =>
                            (prev - 1 + cityPrograms.length) %
                            cityPrograms.length,
                        )
                      }
                    >
                      ❮
                    </button>
                    <button
                      className="btn btn-circle btn-sm absolute right-0 top-1/2 -translate-y-1/2"
                      onClick={() =>
                        setCurrentCitySlide(
                          (prev) => (prev + 1) % cityPrograms.length,
                        )
                      }
                    >
                      ❯
                    </button>

                    {/* Dots Navigation */}
                    <div className="mt-6 flex justify-center gap-2">
                      {cityPrograms.map((_, index) => (
                        <button
                          className={`h-3 w-3 rounded-full transition-all ${
                            currentCitySlide === index
                              ? "w-8 bg-primary"
                              : "bg-base-content/30"
                          }`}
                          key={index}
                          onClick={() => setCurrentCitySlide(index)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Partnerships */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">
                Strategic Partnerships
              </h2>
              <p className="text-xl text-base-content/70">
                Collaborating with organizations to maximize community impact
              </p>
            </div>

            {partnershipLoading ? (
              <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : partnershipError ? (
              <div className="alert alert-error">
                <span>{partnershipError}</span>
              </div>
            ) : (
              <>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                  {visiblePartnerships.map((partner) => (
                    <div
                      className="card bg-base-200 shadow-lg"
                      key={partner.id}
                    >
                      <div className="card-body text-center">
                        {partner.logoUrl ? (
                          <div className="mb-3">
                            <img
                              alt={partner.organizationName}
                              className="mx-auto h-16 w-16 object-contain"
                              src={`${import.meta.env.VITE_API_STORAGE_URL}/storage/${partner.logoUrl}`}
                            />
                          </div>
                        ) : (
                          <div className="mb-3 text-4xl">🤝</div>
                        )}
                        <h3 className="card-title justify-center text-lg">
                          {partner.organizationName}
                        </h3>
                        <div className="badge badge-sm mb-2">
                          {partner.partnershipType}
                        </div>
                        <p className="text-sm text-base-content/70">
                          {partner.description}
                        </p>
                        {partner.website && (
                          <a
                            className="btn btn-link btn-sm mt-2"
                            href={partner.website}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            Visit Website
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {totalPartnershipSlides > 1 && (
                  <div className="mt-8 flex justify-center">
                    <div className="join">
                      <button
                        className="btn join-item"
                        disabled={currentPartnershipSlide === 0}
                        onClick={() =>
                          setCurrentPartnershipSlide((prev) =>
                            Math.max(0, prev - 1),
                          )
                        }
                      >
                        «
                      </button>
                      {Array.from(
                        { length: totalPartnershipSlides },
                        (_, i) => i,
                      ).map((slideIndex) => (
                        <button
                          className={`btn join-item ${
                            currentPartnershipSlide === slideIndex
                              ? "btn-active"
                              : ""
                          }`}
                          key={slideIndex}
                          onClick={() => setCurrentPartnershipSlide(slideIndex)}
                        >
                          {slideIndex + 1}
                        </button>
                      ))}
                      <button
                        className="btn join-item"
                        disabled={
                          currentPartnershipSlide === totalPartnershipSlides - 1
                        }
                        onClick={() =>
                          setCurrentPartnershipSlide((prev) =>
                            Math.min(totalPartnershipSlides - 1, prev + 1),
                          )
                        }
                      >
                        »
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Community Voices</h2>
              <p className="text-xl text-base-content/70">
                Hear from community leaders about our impact
              </p>
            </div>

            {voicesLoading ? (
              <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : voicesError ? (
              <div className="alert alert-error">
                <span>{voicesError}</span>
              </div>
            ) : (
              <div className="relative">
                <div className="overflow-hidden">
                  <div
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{
                      transform: `translateX(-${currentVoiceSlide * 100}%)`,
                    }}
                  >
                    {communityVoices.map((voice) => (
                      <div className="w-full flex-shrink-0 px-4" key={voice.id}>
                        <div className="card mx-auto max-w-2xl bg-base-100 shadow-lg">
                          <div className="card-body">
                            <div className="mb-4 flex items-center gap-4">
                              {voice.avatarUrl ? (
                                <img
                                  alt={voice.avatarAlt || voice.name}
                                  className="h-16 w-16 rounded-full object-cover"
                                  src={`${import.meta.env.VITE_API_STORAGE_URL}/storage/${voice.avatarUrl}`}
                                />
                              ) : (
                                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-2xl text-primary-content">
                                  {voice.name.charAt(0)}
                                </div>
                              )}
                              <div>
                                <h4 className="font-semibold">{voice.name}</h4>
                                <p className="text-sm text-base-content/70">
                                  {voice.title}
                                </p>
                              </div>
                            </div>
                            <p className="text-center text-lg italic text-base-content/80">
                              "{voice.testimonial}"
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {communityVoices.length > 1 && (
                  <>
                    {/* Navigation Buttons */}
                    <button
                      className="btn btn-circle btn-sm absolute left-0 top-1/2 -translate-y-1/2"
                      onClick={() =>
                        setCurrentVoiceSlide(
                          (prev) =>
                            (prev - 1 + communityVoices.length) %
                            communityVoices.length,
                        )
                      }
                    >
                      ❮
                    </button>
                    <button
                      className="btn btn-circle btn-sm absolute right-0 top-1/2 -translate-y-1/2"
                      onClick={() =>
                        setCurrentVoiceSlide(
                          (prev) => (prev + 1) % communityVoices.length,
                        )
                      }
                    >
                      ❯
                    </button>

                    {/* Dots Navigation */}
                    <div className="mt-6 flex justify-center gap-2">
                      {communityVoices.map((_, index) => (
                        <button
                          className={`h-3 w-3 rounded-full transition-all ${
                            currentVoiceSlide === index
                              ? "w-8 bg-primary"
                              : "bg-base-content/30"
                          }`}
                          key={index}
                          onClick={() => setCurrentVoiceSlide(index)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Impact Stats */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl font-bold">Our Impact in Numbers</h2>
            </div>

            {statsLoading ? (
              <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : statsError ? (
              <div className="alert alert-error">
                <span>{statsError}</span>
              </div>
            ) : (
              <>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                  {visibleStats.map((stat) => (
                    <div
                      className="stat rounded-lg bg-base-200 shadow-lg"
                      key={stat.id}
                    >
                      <div className={`stat-figure text-${stat.color}`}>
                        {getIconComponent(stat.icon)}
                      </div>
                      <div className="stat-title">{stat.title}</div>
                      <div className={`stat-value text-${stat.color}`}>
                        {stat.value}
                      </div>
                      <div className="stat-desc">{stat.description}</div>
                    </div>
                  ))}
                </div>

                {totalStatSlides > 1 && (
                  <div className="mt-8 flex justify-center">
                    <div className="join">
                      <button
                        className="btn join-item"
                        disabled={currentStatSlide === 0}
                        onClick={() =>
                          setCurrentStatSlide((prev) => Math.max(0, prev - 1))
                        }
                      >
                        «
                      </button>
                      {Array.from({ length: totalStatSlides }, (_, i) => i).map(
                        (slideIndex) => (
                          <button
                            className={`btn join-item ${
                              currentStatSlide === slideIndex
                                ? "btn-active"
                                : ""
                            }`}
                            key={slideIndex}
                            onClick={() => setCurrentStatSlide(slideIndex)}
                          >
                            {slideIndex + 1}
                          </button>
                        ),
                      )}
                      <button
                        className="btn join-item"
                        disabled={currentStatSlide === totalStatSlides - 1}
                        onClick={() =>
                          setCurrentStatSlide((prev) =>
                            Math.min(totalStatSlides - 1, prev + 1),
                          )
                        }
                      >
                        »
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
